import React, { useMemo } from "react"

import { ButtonElement } from "@front-shared/elements/button.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { FloatingActionBarElement } from "../../../elements/floatingActionBar.element"
import type { IEraUploadBatchSummary } from "../../../types/integrations.types"

import plusIcon from "@front-shared/assets/images/icons/plus-dark.svg"

export const EraUploadReviewFloatingControlComponent: React.FunctionComponent<
    IEraUploadReviewFloatingControlComponentProps
> = props => {
    const statusLabel = useMemo(() => {
        const summary = props.summary

        if (props.isUploading) {
            return "Uploading files..."
        }

        if (summary.failedUploadFileCount > 0) {
            return `${summary.succeededFileCount} uploaded · ${summary.failedUploadFileCount} failed`
        }

        if (summary.succeededFileCount > 0) {
            return `${summary.succeededFileCount} of ${summary.validFileCount} uploaded`
        }

        if (summary.invalidFileCount > 0) {
            return `${summary.totalFileCount} file${summary.totalFileCount === 1 ? "" : "s"} · ${summary.validFileCount} ready · ${summary.invalidFileCount} need attention`
        }

        return `${summary.validFileCount} file${summary.validFileCount === 1 ? "" : "s"} ready`
    }, [props.summary, props.isUploading])

    const uploadLabel = useMemo(() => {
        const count = props.summary.uploadableFileCount

        if (count === 0) {
            return "Upload to secure storage"
        }

        return `Upload ${count} file${count === 1 ? "" : "s"}`
    }, [props.summary.uploadableFileCount])

    const showSuccessIcon =
        !props.isUploading && props.summary.failedUploadFileCount === 0

    return (
        <FloatingActionBarElement className="gap-[16px]">
            <div className="flex items-center gap-[8px] flex-shrink-0 min-w-0">
                {showSuccessIcon ? (
                    <span
                        className="
                            flex-shrink-0 w-[20px] h-[20px] rounded-full flex items-center justify-center
                            bg-brand-green3 text-white text-[12px] font-bold
                        "
                        aria-hidden
                    >
                        ✓
                    </span>
                ) : null}

                <PrimaryText
                    typography="text"
                    size={14}
                    className="whitespace-nowrap"
                >
                    {statusLabel}
                </PrimaryText>
            </div>

            <div className="flex items-center gap-[16px] flex-shrink-0">
                {props.canAddFiles ? (
                    <ButtonElement
                        label="Add files"
                        htmlType="button"
                        type="default"
                        onClick={props.onAddFiles}
                        icon={plusIcon}
                        buttonClassName="!pl-[16px] !pr-[16px]"
                        disabled={props.isUploading}
                    />
                ) : null}

                <ButtonElement
                    label={uploadLabel}
                    htmlType="button"
                    type="primary"
                    onClick={props.onUpload}
                    isLoading={props.isUploading}
                    buttonClassName="!pl-[16px] !pr-[16px]"
                    disabled={
                        props.isUploading ||
                        props.summary.uploadableFileCount === 0
                    }
                />
            </div>
        </FloatingActionBarElement>
    )
}

interface IEraUploadReviewFloatingControlComponentProps {
    summary: IEraUploadBatchSummary
    canAddFiles: boolean
    onAddFiles: () => void
    onUpload: () => void
    isUploading?: boolean
}
